/**
 * Campagnes WhatsApp réseau (SuperAdmin, brique G) — LOGIQUE PURE.
 *
 * Prépare une diffusion vers la base clients opt-in (réseau ou un resto) : sélection
 * des destinataires, message personnalisé ({prenom}), lien wa.me. L'envoi en masse
 * passera par l'API WhatsApp Business Cloud (séparé). Aucune I/O ici.
 */
import { displayName, type NetworkCustomer } from './networkCrm';

export type Audience = 'network' | 'tenant';

export interface CampaignDraft {
  title: string;
  message: string;
  audience: Audience;
  /** resto ciblé si audience = 'tenant'. */
  tenantId: string | null;
}

/** Un destinataire prêt à contacter. */
export interface Recipient {
  name: string;
  firstName: string;
  phone: string;
}

/**
 * Sélectionne les destinataires opt-in avec un téléphone, selon l'audience :
 *  - 'network' : tous les clients ayant accepté la com réseau (network_consent).
 *  - 'tenant'  : clients d'un resto donné ayant accepté la com de CE resto (marketing_consent).
 */
export function selectRecipients(
  customers: NetworkCustomer[],
  audience: Audience,
  tenantId: string | null,
): Recipient[] {
  const out: Recipient[] = [];
  for (const c of customers) {
    if (!c.phone.trim()) continue;
    let eligible = false;
    if (audience === 'network') {
      eligible = c.networkConsent;
    } else if (audience === 'tenant' && tenantId) {
      eligible = c.tenants.some((t) => t.tenantId === tenantId && t.marketingConsent);
    }
    if (!eligible) continue;
    out.push({ name: displayName(c), firstName: c.firstName || displayName(c), phone: c.phone });
  }
  return out;
}

/** Remplit le message en remplaçant {prenom} (et {nom}) par les valeurs du destinataire. */
export function fillTemplate(message: string, r: Pick<Recipient, 'firstName' | 'name'>): string {
  return message
    .replace(/\{prenom\}/gi, r.firstName)
    .replace(/\{nom\}/gi, r.name);
}

/**
 * Normalise un numéro pour wa.me : chiffres seulement, préfixe pays.
 * Un numéro FR à 10 chiffres commençant par 0 devient 33XXXXXXXXX.
 * Renvoie '' si trop court pour être valide.
 */
export function normalizePhone(phone: string, defaultCc = '33'): string {
  let digits = phone.replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2); // 0033… → 33…
  if (digits.length === 10 && digits.startsWith('0')) digits = defaultCc + digits.slice(1);
  return digits.length >= 8 ? digits : '';
}

/** Construit le lien wa.me (clic-pour-discuter) avec le message pré-rempli. */
export function waMeLink(phone: string, message: string): string {
  const num = normalizePhone(phone);
  if (!num) return '';
  return `https://wa.me/${num}?text=${encodeURIComponent(message)}`;
}

/** Un brouillon est-il prêt à être enregistré ? (titre + message + au moins 1 destinataire). */
export function isCampaignReady(draft: CampaignDraft, recipientCount: number): boolean {
  return draft.title.trim() !== '' && draft.message.trim() !== '' && recipientCount > 0;
}

export const AUDIENCE_LABELS: Record<Audience, string> = {
  network: 'Réseau (opt-in Lil & Co)',
  tenant: 'Un resto (opt-in resto)',
};

/** Forme brute d'une campagne renvoyée par la RPC. */
export interface CampaignRow {
  id: string;
  title: string;
  message: string;
  audience: Audience;
  tenantId: string | null;
  recipientCount: number;
  status: 'draft' | 'ready' | 'sent';
  createdAt: string | null;
}

interface RawCampaign {
  id?: string;
  title?: string | null;
  message?: string | null;
  audience?: string | null;
  tenant_id?: string | null;
  recipient_count?: number | null;
  status?: string | null;
  created_at?: string | null;
}

/** Parse la réponse jsonb de `list_whatsapp_campaigns`. */
export function parseCampaigns(data: unknown): CampaignRow[] {
  if (!Array.isArray(data)) return [];
  return (data as RawCampaign[]).map((r) => ({
    id: r.id ?? '',
    title: r.title ?? '',
    message: r.message ?? '',
    audience: r.audience === 'tenant' ? 'tenant' : 'network',
    tenantId: r.tenant_id ?? null,
    recipientCount: r.recipient_count ?? 0,
    status: r.status === 'sent' ? 'sent' : r.status === 'ready' ? 'ready' : 'draft',
    createdAt: r.created_at ?? null,
  }));
}
